import React from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  Background,
  useReactFlow,
} from "@xyflow/react";

import "@xyflow/react/dist/style.css";
import { useParams } from "react-router-dom";
import { useSite } from "../../../store/useSite";
import SizeShow from "../../booths/SizeShow";
import { useBase } from "../../../store/useBase";

const FloorMapContent = ({
  previewNodes,
  nodeTypes,
  setSelectedBooth,
  setIsBoothPanelOpen,
  selectedFloorData,
}) => {
  const { fitView, fitBounds } = useReactFlow();

  React.useEffect(() => {
    if (!selectedFloorData) return;

    const timer = setTimeout(() => {
      const focusAreas = selectedFloorData.focus_areas || [];
      const mainFocus =
        focusAreas.find((f) => f.status === "active") || focusAreas[0];

      if (mainFocus) {
        fitBounds(
          {
            x: mainFocus.focus_x,
            y: mainFocus.focus_y,
            width: mainFocus.focus_width,
            height: mainFocus.focus_height,
          },
          { padding: 0.1, duration: 800 },
        );
      } else {
        fitView({ padding: 0.2, duration: 800 });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [selectedFloorData?.id, fitView, fitBounds]);

  return (
    <ReactFlow
      nodes={previewNodes}
      edges={[]}
      nodeTypes={nodeTypes}
      panOnScroll={true}
      zoomOnScroll={true}
      panOnDrag={true}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={true}
      onNodeClick={(e, node) => {
        if (
          node.type === "booth" &&
          (node.data?.status === "available" || !node.data?.status)
        ) {
          setSelectedBooth(node);
          setIsBoothPanelOpen(true);
        }
      }}
      minZoom={0.2}
      maxZoom={4}
    >
      <Background color="#ccc" gap={20} />
      <Controls showInteractive={false} position="bottom-right" />
    </ReactFlow>
  );
};

function FloorPlanSection({
  availableBooths,
  bookedBooths,
  onHoldBooths,
  selectedFloor,
  setSelectedFloor,
  setIsFullPagePreview,
  floors,
  previewNodes,
  nodeTypes,
  setSelectedBooth,
  setIsBoothPanelOpen,
  isBoothPanelOpen,
  selectedBooth,
  RightSidePanel,
  categories,
  renderBenefit,
  handleApplyFromBooth,
  eventSettings,
}) {
  const { slug } = useParams();
  const { eventId } = useBase();
  const { eventData } = useSite();

  const selectedFloorData = React.useMemo(
    () => floors.find((f) => f.id === selectedFloor),
    [floors, selectedFloor],
  );

  return (
    <section className="bg-[#6ebf8b] py-12 md:py-16 px-4 md:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-white mb-2">Floor Plan</h2>
          <p className="text-white/90 text-sm">
            Refer to the floor plan to view the layout of the event
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-xl overflow-hidden flex flex-col h-[500px] sm:h-[600px] transition-all duration-300">
          <div className="px-4 py-3 md:px-6 md:py-4 flex flex-col sm:flex-row sm:items-center justify-between border-b border-gray-100 bg-white gap-4">
            <div className="flex flex-wrap items-center gap-4 md:gap-6">
              <div className="flex items-center gap-2">
                <div
                  className="w-3.5 h-3.5 rounded-full border border-gray-200 shadow-sm"
                  style={{
                    backgroundColor:
                      eventSettings?.booth_color_available || "#ffffff",
                  }}
                />
                <span className="text-[11px] md:text-xs font-bold text-gray-500 uppercase tracking-tight">
                  Available
                </span>
                {/* ({availableBooths}) */}
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-3.5 h-3.5 rounded-full shadow-sm"
                  style={{
                    backgroundColor:
                      eventSettings?.booth_color_assigned || "#FF69B4",
                  }}
                />
                <span className="text-[11px] md:text-xs font-bold text-gray-500 uppercase tracking-tight">
                  Booked
                </span>
                {/* ({bookedBooths}) */}
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-3.5 h-3.5 rounded-full shadow-sm"
                  style={{
                    backgroundColor:
                      eventSettings?.booth_color_reserved || "#FFB6C1",
                  }}
                />
                <span className="text-[11px] md:text-xs font-bold text-gray-500 uppercase tracking-tight">
                  ON HOLD
                </span>
                {/* ({onHoldBooths}) */}
              </div>
            </div>

            <div className="flex items-center gap-4 mt-4 sm:mt-0">
              <select
                value={selectedFloor}
                onChange={(e) => setSelectedFloor(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {floors.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>

              <button
                onClick={() => {
                  const eventSlug = slug || eventData?.slug || "event";
                  window.open(
                    `/${eventSlug}/exhibitor-floor-plan/${eventId}`,
                    "_blank",
                  );
                }}
                className="text-blue-600 font-semibold text-xs hover:text-blue-800 transition-colors"
              >
                View in Full Page
              </button>
            </div>
          </div>

          <div className="flex-1 relative bg-[#fafafa]">
            <ReactFlowProvider>
              <FloorMapContent
                previewNodes={previewNodes}
                nodeTypes={nodeTypes}
                setSelectedBooth={setSelectedBooth}
                setIsBoothPanelOpen={setIsBoothPanelOpen}
                selectedFloorData={selectedFloorData}
              />
            </ReactFlowProvider>
          </div>

          {/* BOOTH SIDE PANEL */}
          {isBoothPanelOpen &&
            selectedBooth &&
            (() => {
              const boothData = selectedBooth.data || {};
              const cat =
                categories?.find(
                  (c) => String(c.id) === String(boothData.category_id),
                ) ||
                categories?.find(
                  (c) =>
                    c.name ===
                    (boothData.category_name || boothData.exhibitorCategory),
                );

              const length =
                boothData.dimensions?.length ||
                selectedBooth?.height ||
                cat?.boothLength;
              const breadth =
                boothData.dimensions?.breadth ||
                selectedBooth?.width ||
                cat?.boothBreadth;
              const unit =
                eventSettings?.booth_dimension_unit ||
                cat?.unit ||
                boothData.dimensions?.unit ||
                "feet";

              // Extract booth passes & dynamic features
              const boothPasses =
                cat?.boothMembers ??
                boothData.benefits?.exhibitorPasses?.quantity ??
                2;
              const features = cat?.features;

              return (
                <RightSidePanel
                  isOpen={isBoothPanelOpen && selectedBooth}
                  onClose={() => setIsBoothPanelOpen(false)}
                  title="Booth Details"
                >
                  <>
                    <div className="space-y-8">
                      <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">
                          Booth Number
                        </label>
                        <span className="text-lg font-bold text-gray-900">
                          {boothData.number || boothData.label || "N/A"}
                        </span>
                      </div>

                      <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">
                          Exhibitor Category
                        </label>
                        <span className="text-sm font-semibold text-gray-700">
                          {cat?.name ||
                            boothData.category_name ||
                            boothData.exhibitorCategory ||
                            "N/A"}
                        </span>
                      </div>

                      <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">
                          Booth Dimensions
                        </label>
                        <span className="text-sm font-semibold text-gray-700 inline-flex">
                          <SizeShow
                            height={length}
                            width={breadth}
                            booth_dimension_unit={unit}
                          />
                        </span>
                      </div>

                      <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">
                          Booth Price
                        </label>
                        <span className="text-sm font-bold text-gray-900">
                          {eventSettings?.currency || cat?.currency || "₹"}{" "}
                          {boothData.price || cat?.price || "0"}
                        </span>
                      </div>

                      <div>
                        <h4 className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-4">
                          Benefits
                        </h4>

                        <div className="space-y-1">
                          {(!features || features.length === 0) &&
                          boothPasses === null ? (
                            <p className="text-xs text-gray-400 italic">
                              No benefits details available
                            </p>
                          ) : (
                            <>
                              {boothPasses !== null &&
                                boothPasses !== undefined &&
                                renderBenefit(
                                  true,
                                  "Booth Members",
                                  boothPasses,
                                )}

                              {features
                                ?.filter((f) => f.visible !== 0)
                                .sort((a, b) => a.order - b.order)
                                .map((feature) => (
                                  <div key={feature.id || feature.key}>
                                    {renderBenefit(
                                      feature.enabled === 1 ||
                                        feature.value === "yes",
                                      feature.name,
                                      feature.supported_values?.includes(
                                        "count",
                                      ) && feature.value !== "no"
                                        ? feature.value
                                        : null,
                                    )}
                                  </div>
                                ))}
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="pt-8">
                      <div className="flex gap-3 pt-3 border-t border-gray-100">
                        <button
                          disabled={
                            boothData.status === "assigned" ||
                            boothData.status === "onHold"
                          }
                          onClick={() => handleApplyFromBooth(selectedBooth)}
                          className="flex-1 px-4 py-2.5 bg-[#4f46e5] hover:bg-indigo-600 disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm font-semibold rounded-lg shadow-sm shadow-indigo-500/20 transition-all flex justify-center items-center"
                        >
                          {boothData.status === "assigned"
                            ? "Assigned"
                            : boothData.status === "onHold"
                              ? "Reserved"
                              : "Apply for Booth"}
                        </button>
                      </div>
                    </div>
                  </>
                </RightSidePanel>
              );
            })()}
        </div>
      </div>
    </section>
  );
}

export default FloorPlanSection;
